import { z } from "zod";
import {
  AbsenceCategory,
  AbsenceCategorySchema,
  Absence,
  CreateAbsenceDto,
  UpdateAbsenceDto,
} from "@myapp/contract";
import {
  isFullDayCategory,
  fullDayRangeToIso,
  fullDayLastDate,
} from "../../../../entities/absence";

export { isFullDayCategory };

export const timeManagementFormSchema = z
  .object({
    technicianIds: z.array(z.coerce.number().int().positive()).min(1, "Mindestens ein Handwerker"),
    category: AbsenceCategorySchema,
    comment: z.string().default(""),
    dateFrom: z.string().default(""),
    dateTo: z.string().default(""),
    date: z.string().default(""),
    timeFrom: z.string().default(""),
    timeTo: z.string().default(""),
  })
  .superRefine((data, ctx) => {
    const isFullDay = isFullDayCategory(data.category);

    if (isFullDay) {
      if (!data.dateFrom) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pflichtfeld", path: ["dateFrom"] });
      }
      if (!data.dateTo) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pflichtfeld", path: ["dateTo"] });
      }
      if (data.dateFrom && data.dateTo && data.dateFrom > data.dateTo) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Von muss vor Bis liegen", path: ["dateTo"] });
      }
    } else {
      if (!data.date) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pflichtfeld", path: ["date"] });
      }
      if (!data.timeFrom) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pflichtfeld", path: ["timeFrom"] });
      }
      if (!data.timeTo) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Pflichtfeld", path: ["timeTo"] });
      }
      if (data.timeFrom && data.timeTo && data.timeFrom >= data.timeTo) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Von muss vor Bis liegen", path: ["timeTo"] });
      }
    }
  });

export type TimeManagementFormValues = z.infer<typeof timeManagementFormSchema>;

export function getDefaultValues(category: AbsenceCategory): TimeManagementFormValues {
  return {
    technicianIds: [],
    category,
    comment: "",
    dateFrom: "",
    dateTo: "",
    date: "",
    timeFrom: "",
    timeTo: "",
  };
}

export function toCreateDto(values: TimeManagementFormValues, force?: boolean): CreateAbsenceDto {
  const isFullDay = isFullDayCategory(values.category);

  if (isFullDay) {
    return {
      technicianIds: values.technicianIds,
      category: values.category,
      comment: values.comment || undefined,
      ...fullDayRangeToIso(values.dateFrom, values.dateTo),
      force,
    };
  }

  return {
    technicianIds: values.technicianIds,
    category: values.category,
    comment: values.comment || undefined,
    startsAt: new Date(`${values.date}T${values.timeFrom}:00`).toISOString(),
    endsAt: new Date(`${values.date}T${values.timeTo}:00`).toISOString(),
    force,
  };
}

export function toUpdateDto(values: TimeManagementFormValues, force?: boolean): UpdateAbsenceDto {
  const isFullDay = isFullDayCategory(values.category);

  if (isFullDay) {
    return {
      technicianIds: values.technicianIds,
      category: values.category,
      comment: values.comment || null,
      ...fullDayRangeToIso(values.dateFrom, values.dateTo),
      force,
    };
  }

  return {
    technicianIds: values.technicianIds,
    category: values.category,
    comment: values.comment || null,
    startsAt: new Date(`${values.date}T${values.timeFrom}:00`).toISOString(),
    endsAt: new Date(`${values.date}T${values.timeTo}:00`).toISOString(),
    force,
  };
}

function toLocalDateString(value: string | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toLocalTimeString(iso: string): string {
  const d = new Date(iso);
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${min}`;
}

export function fromTimeManagementDetail(
  item: Absence,
): TimeManagementFormValues {
  const isFullDay = isFullDayCategory(item.category);
  const startDate = toLocalDateString(item.startsAt);
  // `endsAt` is exclusive — the form's "Bis" is the last absent day, one instant earlier.
  const endDate = toLocalDateString(fullDayLastDate(item.endsAt));
  const startTime = toLocalTimeString(item.startsAt);
  const endTime = toLocalTimeString(item.endsAt);

  return {
    technicianIds: item.technicians.map((t) => t.id),
    category: item.category,
    comment: item.comment ?? "",
    dateFrom: isFullDay ? startDate : "",
    dateTo: isFullDay ? endDate : "",
    date: isFullDay ? "" : startDate,
    timeFrom: isFullDay ? "" : startTime,
    timeTo: isFullDay ? "" : endTime,
  };
}
